if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.currentEnrichedVariant = null;

        // Add event listener for product-info:loaded event
        this.addEventListener('product-info:loaded', ({ target }) => {
          if (target && typeof target.addPreProcessCallback === 'function') {
            target.addPreProcessCallback(this.preprocessHTML.bind(this));
          }
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification && typeof cartNotification.setActiveElement === 'function') {
          cartNotification.setActiveElement(this.openedBy);
        }

        // Store current scroll position
        const scrollPosition = window.scrollY;

        // Clean up any nested modals first
        const nestedModals = this.modalContent.querySelectorAll('.inla-modal[style*="visible"]');
        nestedModals.forEach((modal) => {
          modal.style.visibility = 'hidden';
          modal.style.pointerEvents = 'none';
          modal.classList.remove('show');
        });

        this.modalContent.innerHTML = '';

        // Find the main product info element
        const mainProductInfo = document.querySelector("product-info[id^='MainProduct']");
        if (mainProductInfo) {
          // Get the current variant data
          const variantJson = mainProductInfo.querySelector('[data-selected-variant]');
          if (variantJson) {
            try {
              const variantData = JSON.parse(variantJson.textContent);

              // Try to find enriched variant data in window.variantData first
              let enrichedVariant = null;
              if (window.variantData && Array.isArray(window.variantData)) {
                enrichedVariant = window.variantData.find((v) => v.id === parseInt(variantData.id, 10));
              }

              // If not found in window.variantData, try to find in the main product's variant data
              if (!enrichedVariant) {
                const mainProductVariantData = mainProductInfo.querySelector('[data-variant-data]');
                if (mainProductVariantData) {
                  try {
                    const mainVariants = JSON.parse(mainProductVariantData.textContent);
                    enrichedVariant = mainVariants.find((v) => v.id === parseInt(variantData.id, 10));
                  } catch (e) {
                    console.error('[Quick Add] Error parsing main product variant data:', e);
                  }
                }
              }

              // If we still don't have enriched data, try to enrich the basic data
              if (!enrichedVariant) {
                const productType =
                  mainProductInfo.getAttribute('data-product-type') ||
                  window.variantData?.find((v) => v.title === variantData.title)?.productType;

                // Find any variant with the same title to get enriched data
                const similarVariant = window.variantData?.find((v) => v.title === variantData.title);

                // Create enriched variant with all necessary properties
                enrichedVariant = {
                  ...variantData,
                  productType: productType,
                  // Copy properties from similar variant if found
                  ...(similarVariant || {}),
                  // Ensure these specific properties are set
                  riderLengthMin: similarVariant?.riderLengthMin,
                  riderLengthMax: similarVariant?.riderLengthMax,
                  maxBootSize: similarVariant?.maxBootSize,
                  boardRadius: similarVariant?.boardRadius,
                  boardWaistWidth: similarVariant?.boardWaistWidth,
                  sizeGuideForBindings: similarVariant?.sizeGuideForBindings,
                  sizeConversion: similarVariant?.sizeConversion,
                };
              }

              // Update the main product's variant info
              if (typeof mainProductInfo._updateCustomVariantInfo === 'function') {
                mainProductInfo._updateCustomVariantInfo(enrichedVariant);
              }

              // Dispatch a variant change event to ensure all components update
              const event = new CustomEvent('variant:change', {
                detail: {
                  variant: enrichedVariant,
                },
              });
              mainProductInfo.dispatchEvent(event);
            } catch (e) {
              console.error('[Quick Add] Error restoring main product variant:', e);
            }
          }
        }

        if (preventFocus) this.openedBy = null;

        // Restore scroll position after a short delay
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 100);

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // Store current scroll position
        const scrollPosition = window.scrollY;

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);

            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Restore scroll position
            window.scrollTo(0, scrollPosition);

            const productInfoElement = this.modalContent.querySelector('product-info');
            if (productInfoElement) {
              productInfoElement.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

              if (typeof productInfoElement._updateCustomVariantInfo === 'function') {
                const variantJson = productInfoElement.querySelector('[data-selected-variant]');
                if (variantJson) {
                  try {
                    const variantData = JSON.parse(variantJson.textContent);
                    productInfoElement._updateCustomVariantInfo(variantData);
                  } catch (e) {
                    console.error('Quick Add Modal: Error parsing variant JSON:', e);
                  }
                }
              }
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .catch((error) => {
            console.error('Quick Add Modal: Error loading product:', error);
            this.hide();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });

        // Preserve the variant data before any processing
        const variantJson = productElement.querySelector('[data-selected-variant]');
        const variantData = variantJson ? variantJson.textContent : null;

        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);

        // Ensure the variant data is preserved after processing
        if (variantData && !productElement.querySelector('[data-selected-variant]')) {
          const newVariantJson = document.createElement('script');
          newVariantJson.setAttribute('type', 'application/json');
          newVariantJson.setAttribute('data-selected-variant', '');
          newVariantJson.textContent = variantData;
          productElement.appendChild(newVariantJson);
        }

        // Add event listener for variant changes
        const variantSelects = productElement.querySelector('variant-selects');
        if (variantSelects) {
          // We need to wait for the element to be in the DOM before adding the listener
          setTimeout(() => {
            const variantSelectsInDOM = this.modalContent.querySelector('variant-selects');
            if (variantSelectsInDOM) {
              variantSelectsInDOM.addEventListener('variant:change', (event) => {
                // Get the product-info element
                const productInfoElement = this.modalContent.querySelector('product-info');
                if (productInfoElement && typeof productInfoElement._updateCustomVariantInfo === 'function') {
                  // Try to find enriched variant data in window.variantData
                  let enrichedVariant = event.detail.variant;
                  if (window.variantData && Array.isArray(window.variantData) && event.detail.variant.id) {
                    const foundVariant = window.variantData.find((v) => v.id === parseInt(event.detail.variant.id, 10));
                    if (foundVariant) {
                      enrichedVariant = foundVariant;
                    }
                  }

                  // Update the custom variant info with the enriched data
                  productInfoElement._updateCustomVariantInfo(enrichedVariant);
                }
              });
            }
          }, 100); // Small delay to ensure the element is in the DOM
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
